/**
 * Class implementing drag-and-drop functionality for tables.
 */
export class Draggable {
    /**
     * Draggable class constructor.
     */
    constructor() {
        this.tables = [];
        this.currentTable = null;
        this.originalOrder = null;
        this.dragPanIcons = new Map();
        this.afterReorderCallbacks = new Map();
        this.controlRows = new Map();
    }

    /**
     * Initializes draggable functionality for all tables with the appropriate attribute.
     */
    init() {
        this.tables = document.querySelectorAll('table[data-dcms-draggable="true"]');
        DCMSDebugLog(`Found ${this.tables.length} tables with draggable attribute`, 3, 'Draggable');
        
        this.tables.forEach((table, index) => {
            DCMSDebugLog(`Initializing table ${index + 1}`, 3, 'Draggable');
            
            const submitUrl = table.dataset.dcmsDraggableSubmitUrl;
            if (!submitUrl) {
                DCMSDebugLog(`Table ${index + 1}: Missing URL for submitting data`, 1, 'Draggable');
                return;
            }

            const afterReorderFn = table.dataset.dcmsDraggableAfterReorder;
            if (afterReorderFn) {
                if (typeof window[afterReorderFn] === 'function') {
                    this.afterReorderCallbacks.set(table, window[afterReorderFn]);
                    DCMSDebugLog(`Table ${index + 1}: AfterReorder function set: ${afterReorderFn}`, 3, 'Draggable');
                } else {
                    DCMSDebugLog(`Table ${index + 1}: Invalid afterReorder function: ${afterReorderFn}`, 1, 'Draggable');
                }
            }

            this._addDragColumn(table);

            const dragPanIcon = this._createIcon('drag_pan', __i18n("dragEnable"));
            dragPanIcon.classList.add('cursor-pointer');
            dragPanIcon.addEventListener('click', () => {
                DCMSDebugLog(`Table ${index + 1}: Drag icon clicked`, 3, 'Draggable');
                this._toggleDraggableMode(table, submitUrl);
            });
            
            const actionsCell = table.querySelector('thead tr th.dcms-table-actions');
            if (actionsCell) {
                actionsCell.appendChild(dragPanIcon);
                this.dragPanIcons.set(table, dragPanIcon);
                DCMSDebugLog(`Table ${index + 1}: Drag icon added to actions cell`, 3, 'Draggable');
            } else {
                DCMSDebugLog(`Table ${index + 1}: Could not find actions cell in table header`, 1, 'Draggable');
            }
        });
    }

    /**
     * Adds a drag-handling column to the table.
     * @param {HTMLTableElement} table - The table to which the column is added.
     * @private
     */
	_addDragColumn(table) {
	    DCMSDebugLog('Adding drag column to table', 3, 'Draggable');
	    const headerRow = table.querySelector('thead tr:last-child');
	    if (headerRow) {
	        const headerCell = document.createElement('th');
	        headerCell.className = 'dcms-table-actions w-10 !p-0';
	        headerCell.setAttribute('scope', 'col');
	        headerRow.insertBefore(headerCell, headerRow.firstChild);
	        DCMSDebugLog('Added header cell for drag column', 3, 'Draggable');
	    } else {
	        DCMSDebugLog('Could not find header row', 1, 'Draggable');
	    }
	
	    const bodyRows = table.querySelectorAll('tbody tr');
	    bodyRows.forEach((row, index) => {
	        const cell = document.createElement('td');
	        cell.className = 'dcms-table-actions w-10 !p-0';
	        if (row.getAttribute('data-dcms-draggable-row') === 'no') {
	            cell.classList.add('dcms-table-actions-disabled');
	        }
	        row.insertBefore(cell, row.firstChild);
	        DCMSDebugLog(`Added drag cell to row ${index + 1}`, 3, 'Draggable');
	    });
	
	    // Copy border styles from the adjacent cell in the same row
	    table.querySelectorAll('.dcms-table-actions').forEach(cell => {
	        const sibling = cell.nextElementSibling;
	        if (sibling) {
	            const borderStyles = window.getComputedStyle(sibling);
	            cell.style.borderTop = borderStyles.borderTop;
	            cell.style.borderBottom = borderStyles.borderBottom;
	        }
	    });
	}

    /**
     * Creates a Material Symbols Rounded icon.
     * @param {string} iconName - Icon name.
     * @param {string} title - Icon title (tooltip).
     * @returns {HTMLSpanElement} - The created icon.
     * @private
     */
    _createIcon(iconName, title) {
        const icon = document.createElement('span');
        icon.classList.add('msr', 'flex', 'items-center', 'justify-center', 'w-full', 'h-full');
        icon.textContent = iconName;
        icon.title = title;
        return icon;
    }
    
    
    

    /**
     * Enables draggable mode for the selected table.
     * @param {HTMLTableElement} table - Table to enable draggable mode for.
     * @param {string} submitUrl - URL to submit the new order to.
     * @private
     */
    _enableDraggableMode(table, submitUrl) {
        DCMSDebugLog('Enabling draggable mode', 2, 'Draggable');
        this.currentTable = table;
        this.originalOrder = this._getTableRowsOrder(table);

        const dragPanIcon = this.dragPanIcons.get(table);
        if (dragPanIcon) {
            dragPanIcon.style.display = 'none';
            DCMSDebugLog('Drag pan icon hidden', 3, 'Draggable');
        }

        const controlRow = this._getOrCreateControlRow(table);
        const draggableCell = this._createDraggableCell(controlRow);
        if (draggableCell) {
            const tableId = table.id;
            draggableCell.innerHTML = `
                <div class="flex justify-end space-x-2">
                    <button id="dcmsDraggable_cancelButton_${tableId}" class="btn size-2 bg-light-pink icon">
                        <i class="msr">undo</i> ${__i18n("dragCancel")}
                    </button>
                    <button id="dcmsDraggable_saveButton_${tableId}" class="btn size-2 bg-light-green icon">
                        <i class="msr">move_up</i> ${__i18n("dragSave")}
                    </button>
                </div>
            `;

            document.getElementById(`dcmsDraggable_saveButton_${tableId}`).addEventListener('click', () => {
                DCMSDebugLog('Save button clicked', 3, 'Draggable');
                this._saveOrder(submitUrl);
            });
            document.getElementById(`dcmsDraggable_cancelButton_${tableId}`).addEventListener('click', () => {
                DCMSDebugLog('Cancel button clicked', 3, 'Draggable');
                this._cancelDraggableMode();
            });
        }

	    table.querySelectorAll('tbody tr').forEach((row, index) => {
	        let dragCell = row.querySelector('td.dcms-table-actions');
	        if (row.getAttribute('data-dcms-draggable-row') !== 'no') {
	            if (!dragCell) {
	                dragCell = document.createElement('td');
	                dragCell.className = 'dcms-table-actions w-10 !p-0';
	                row.insertBefore(dragCell, row.firstChild);
	                DCMSDebugLog(`Created new drag cell for row ${index + 1}`, 3, 'Draggable');
	            }
	            const dragIcon = this._createIcon('drag_indicator', __i18n("dragTip"));
	            dragIcon.classList.add('cursor-move');
	            dragCell.innerHTML = ''; // Clear existing content
	            dragCell.appendChild(dragIcon);
	            row.setAttribute('draggable', 'true');
	            row.classList.add('cursor-move');
	            row.addEventListener('dragstart', (e) => this._dragStart(e, table));
	            row.addEventListener('dragover', this._dragOver);
	            row.addEventListener('drop', (e) => this._drop(e, table));
	            DCMSDebugLog(`Drag icon added to row ${index + 1}`, 3, 'Draggable');
	        } else if (dragCell) {
	            dragCell.innerHTML = ''; // Clear content for non-draggable rows
	            dragCell.classList.add('dcms-table-actions-disabled');
	            row.removeAttribute('draggable');
	            row.classList.remove('cursor-move');
	        }
	    });
    
    }

    /**
     * Sets whether a given row is draggable.
     * @param {HTMLTableRowElement} row - Row for which we set the draggable capability.
     * @param {boolean} draggable - Whether the row should be draggable.
     */
    setRowDraggable(row, draggable) {
        if (draggable) {
            row.removeAttribute('data-dcms-draggable-row');
        } else {
            row.setAttribute('data-dcms-draggable-row', 'no');
        }
       
    }


	/**
	 * Gets the existing control row or creates a new one.
	 * @param {HTMLTableElement} table - Table for which we get/create the control row.
	 * @returns {HTMLTableRowElement} - The control row.
	 * @private
	 */
	_getOrCreateControlRow(table) {
	    let controlRow = this.controlRows.get(table);
	    if (!controlRow) {
	        controlRow = table.querySelector('.dcms-control-row');
	        if (!controlRow) {
	            controlRow = document.createElement('tr');
	            controlRow.className = 'dcms-control-row';
	            const cell = document.createElement('th');
	            cell.colSpan = table.querySelector('thead tr').children.length;
	            cell.className = 'p-2';
	            controlRow.appendChild(cell);
	            const thead = table.querySelector('thead');
	            thead.appendChild(controlRow);
	        }
	        this.controlRows.set(table, controlRow);
	    }
	    return controlRow;
	}
	
	/**
	 * Creates the cell for draggable controls in the control row.
	 * @param {HTMLTableRowElement} controlRow - The control row.
	 * @returns {HTMLElement} - Cell for the draggable controls.
	 * @private
	 */
	_createDraggableCell(controlRow) {
	    const cell = controlRow.querySelector('th');
	    if (!cell) {
	        DCMSDebugLog('Could not find th element in control row', 1, 'Draggable');
	        return null;
	    }
	    let draggableCell = cell.querySelector('.dcms-draggable-controls');
	    if (!draggableCell) {
	        draggableCell = document.createElement('div');
	        draggableCell.className = 'dcms-draggable-controls';
	        cell.appendChild(draggableCell);
	    }
	    return draggableCell;
	}

   
    /**
     * Toggles draggable mode for the selected table.
     * @param {HTMLTableElement} table - Table to toggle draggable mode for.
     * @param {string} submitUrl - URL to submit the new order to.
     * @private
     */
    _toggleDraggableMode(table, submitUrl) {
        DCMSDebugLog('Toggling draggable mode', 3, 'Draggable');
        if (this.currentTable === table) {
            DCMSDebugLog('Disabling draggable mode', 3, 'Draggable');
            this._disableDraggableMode();
        } else {
            if (this.currentTable) {
                DCMSDebugLog('Disabling draggable mode for current table', 3, 'Draggable');
                this._disableDraggableMode();
            }
            DCMSDebugLog('Enabling draggable mode', 3, 'Draggable');
            this._enableDraggableMode(table, submitUrl);
        }
    }

    /**
     * Gets the current order of table rows.
     * @param {HTMLTableElement} table - Table from which the order is read.
     * @returns {string[]} - Array of row IDs in the current order.
     * @private
     */
    _getTableRowsOrder(table) {
        return Array.from(table.querySelectorAll('tbody tr'))
            .map(row => row.dataset.rowId);
    }

	/**
	 * Handles the start of dragging.
	 * @param {DragEvent} e - Drag event.
	 * @param {HTMLTableElement} table - Table the dragged row belongs to.
	 * @private
	 */
	_dragStart(e, table) {
	    e.dataTransfer.setData('text/plain', e.target.closest('tr').dataset.rowId);
	    e.dataTransfer.setData('application/x-table-id', table.id);
	    e.target.closest('tr').classList.add('ring-offset-2', 'ring-4', 'ring-pink-500');
	}

    /**
     * Handles dragging over an element.
     * @param {DragEvent} e - Drag event.
     * @private
     */
    _dragOver(e) {
        e.preventDefault();
    }

	/**
	 * Handles dropping the dragged element.
	 * @param {DragEvent} e - Drag event.
	 * @param {HTMLTableElement} table - Table the dropped row belongs to.
	 * @private
	 */
	_drop(e, table) {
	    e.preventDefault();
	    const draggedId = e.dataTransfer.getData('text/plain');
	    const sourceTableId = e.dataTransfer.getData('application/x-table-id');
	    
	    if (sourceTableId !== table.id) {
	        DCMSDebugLog('Dropped row belongs to a different table', 2, 'Draggable');
	        return;
	    }
	
	    const draggedRow = table.querySelector(`tr[data-row-id="${draggedId}"]`);
	    const dropTarget = e.target.closest('tr');
	
	    if (draggedRow && dropTarget && draggedRow !== dropTarget) {
	        const tbody = table.querySelector('tbody');
	        if (dropTarget.nextSibling) {
	            tbody.insertBefore(draggedRow, dropTarget.nextSibling);
	        } else {
	            tbody.appendChild(draggedRow);
	        }
	    }
	
	    table.querySelectorAll('tr').forEach(row => {
	        row.classList.remove('ring-offset-2', 'ring-4');
	    });
	}

    /**
     * Saves the new row order.
     * @param {string} submitUrl - URL to submit the new order to.
     * @private
     */
    _saveOrder(submitUrl) {
        const newOrder = this._getTableRowsOrder(this.currentTable);

        fetch(submitUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ order: newOrder }),
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                APP.modalsInstance.showModalAlert(__i18n("dragSuccess"), "Success");
                
                const afterReorderCallback = this.afterReorderCallbacks.get(this.currentTable);
                if (afterReorderCallback) {
                    DCMSDebugLog('Executing afterReorder callback', 2, 'Draggable');
                    afterReorderCallback(newOrder);
                }

                this._disableDraggableMode();
            } else {
                DCMSDebugLog("Error saving new order", 1, 'Draggable');
                APP.modalsInstance.showModalAlert(__i18n("dragError"), "Error");
            }
        })
        .catch(error => {
            DCMSDebugLog(`Error: ${error}`, 1, 'Draggable');
            APP.modalsInstance.showModalAlert(__i18n("dragError"), "Error");
        });
    }

    /**
     * Cancels draggable mode and restores the original order.
     * @private
     */
    _cancelDraggableMode() {
        DCMSDebugLog('Cancelling draggable mode', 2, 'Draggable');
        if (!this.currentTable || !this.originalOrder) return;

        const tbody = this.currentTable.querySelector('tbody');
        this.originalOrder.forEach(rowId => {
            const row = tbody.querySelector(`tr[data-row-id="${rowId}"]`);
            if (row) tbody.appendChild(row);
        });
        this._disableDraggableMode();
    }

	/**
	 * Disables draggable mode and cleans up added elements.
	 * @private
	 */
	_disableDraggableMode() {
	    DCMSDebugLog('Disabling draggable mode', 2, 'Draggable');
	    if (!this.currentTable) {
	        DCMSDebugLog('No current table, nothing to disable', 3, 'Draggable');
	        return;
	    }
	
	    const dragPanIcon = this.dragPanIcons.get(this.currentTable);
	    if (dragPanIcon) {
	        dragPanIcon.style.display = '';
	        DCMSDebugLog('Drag pan icon shown', 3, 'Draggable');
	    }
	
	    const controlRow = this.controlRows.get(this.currentTable);
	    if (controlRow) {
	        const draggableCell = controlRow.querySelector('.dcms-draggable-controls');
	        if (draggableCell) {
	            draggableCell.remove();
	            DCMSDebugLog('Draggable controls removed', 3, 'Draggable');
	        }
	        if (controlRow.querySelector('th').children.length === 0) {
	            controlRow.remove();
	            this.controlRows.delete(this.currentTable);
	            DCMSDebugLog('Empty control row removed', 3, 'Draggable');
	        }
	    }
	
	    this.currentTable.querySelectorAll('tbody tr').forEach((row, index) => {
	        const dragCell = row.querySelector('td.dcms-table-actions');
	        if (dragCell) {
	            dragCell.innerHTML = '';
	            DCMSDebugLog(`Drag cell cleared in row ${index + 1}`, 3, 'Draggable');
	        }
	        row.removeAttribute('draggable');
	        row.classList.remove('cursor-move');
	        row.removeEventListener('dragstart', this._dragStart);
	        row.removeEventListener('dragover', this._dragOver);
	        row.removeEventListener('drop', this._drop);
	    });
	
	    this.currentTable = null;
	    this.originalOrder = null;
	    DCMSDebugLog('Draggable mode fully disabled', 3, 'Draggable');
	}
}